import {
  Box,
  Button,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  Typography,
} from "@mui/material";
import { FormikProvider, useFormik, useFormikContext } from "formik";
import { useForUserType, useURLParamsSpreadsheets } from "hooks";
import { shallowEqual, useDispatch, useSelector } from "react-redux";
import { assignmentsLoadAssignments } from "redux/slices/assignments.slice";
import { IRootState } from "redux/types";
import { UserType } from "service";
import * as Yup from "yup";

const limit = 10;

const assignmentTypes = [
  { value: "digital", name: "Оцифровка" },
  { value: "finance", name: "Фин. часть" },
  { value: "legal", name: "Юр. часть" },
];

const applicationYears = [
  { value: "2023", name: "2023" },
  { value: "2024", name: "2024" }
];

const SetupSelect = ({
  label,
  name,
  options,
}: {
  label: string;
  name: string;
  options: { label: string; value: string }[];
}) => {
  const { values, handleChange } = useFormikContext<{
    [key: string]: string;
  }>();

  return (
    <FormControl
      size="small"
      sx={{
        minWidth: "200px",
      }}
    >
      <InputLabel id="refund-type-label">{label}</InputLabel>
      <Select
        labelId="refund-type-label"
        id="refund-type-select"
        name={name}
        value={values[name]}
        onChange={handleChange}
        sx={{
          borderRadius: "8px",
        }}
      >
        <MenuItem value="">
          <em>Не выбран</em>
        </MenuItem>
        {options.map((el) => {
          return (
            <MenuItem key={el.value} value={el.value}>
              {el.label}
            </MenuItem>
          );
        })}
      </Select>
    </FormControl>
  );
};

export const AssignmentsRegistryFilterBlock = () => {
  const dispatch = useDispatch();
  const { assignments, managers } = useSelector(
    (state: IRootState) => state.assignments,
    shallowEqual
  );

  const { userType } = useForUserType();
  const { page, companyName, applicationNo, assignmentType, assigneeId, applicationYear } =
    useURLParamsSpreadsheets();

  const formik = useFormik({
    initialValues: {
      application_no: applicationNo.get(),
      assignment_type: assignmentType.get(),
      company_name: companyName.get(),
      assignee_id: assigneeId.get(),
      application_year: applicationYear.get()
    },
    validationSchema: Yup.object().shape({
      applicationNo: Yup.number().typeError("Неверный формат, только цифры"),
    }),
    onSubmit: async (values) => {
      dispatch(
        assignmentsLoadAssignments({
          limit: limit,
          offset: (page.get() - 1) * limit,
          userType: userType,
          application_no: Number(values.application_no),
          assignment_type: values.assignment_type,
          company_name: values.company_name,
          assignee_id: values.assignee_id,
          application_year: values.application_year
        })
      );

      page.set("1");

      if (values.company_name) companyName.set(values.company_name);
      else companyName.rm();

      if (values.application_no) applicationNo.set(values.application_no);
      else applicationNo.rm();

      if (values.assignment_type) assignmentType.set(values.assignment_type);
      else assignmentType.rm();

      if (values.assignee_id) assigneeId.set(values.assignee_id);
      else assigneeId.rm();

      if (values.application_year) applicationYear.set(values.application_year);
      else applicationYear.rm();
    },
  });

  const isFilterAccepted = () => {
    if (formik.values.company_name != companyName.get()) return false;
    if (formik.values.application_no != applicationNo.get()) return false;
    if (formik.values.assignment_type != assignmentType.get()) return false;
    if (formik.values.assignee_id != assigneeId.get()) return false;
    if (formik.values.application_year != applicationYear.get()) return false;
    return true;
  };

  return (
    <Box>
      <Typography fontSize={"16px"} lineHeight={"20px"} fontWeight={"600"}>
        Фильтр
      </Typography>
      <Typography
        sx={{
          display: isFilterAccepted() ? "none" : "block",
        }}
        color={"red"}
      >
        Указанный вами фильтр не применен. Нажмите кнопку "Применить фильтр".
      </Typography>
      <Box mt={"8px"}>
        <form onSubmit={formik.handleSubmit}>
          <Box>
            <Box
              sx={{
                display: "flex",
                flexDirection: "row",
                flexWrap: "wrap",
                gap: "8px",
              }}
            >
              <TextField
                label="Номер заявки"
                size="small"
                name="application_no"
                error={formik.errors["application_no"] ? true : false}
                value={formik.values.application_no}
                onChange={formik.handleChange}
                placeholder="Введите номер заявки"
              />
              <TextField
                label="Название компании"
                size="small"
                name="company_name"
                error={formik.errors["company_name"] ? true : false}
                value={formik.values.company_name}
                onChange={formik.handleChange}
                placeholder="Введите название компании"
              />
              <FormikProvider value={formik}>
                <SetupSelect
                  label="Тип возмещения"
                  name="assignment_type"
                  options={assignmentTypes.map((el) => {
                    return {
                      label: el.name,
                      value: el.value,
                    };
                  })}
                />
              </FormikProvider>

              
              <FormikProvider value={formik}>
                <SetupSelect
                  label="Год"
                  name="application_year"
                  options={applicationYears.map((el) => {
                    return {
                      label: el.name,
                      value: el.value,
                    };
                  })}
                />
              </FormikProvider>
              

              {userType == UserType.Admin && (
                <FormikProvider value={formik}>
                  <SetupSelect
                    label="Ответственный"
                    name="assignee_id"
                    options={
                      managers.value?.map((el) => {
                        return {
                          label: el.full_name,
                          value: el.user_id ?? "",
                        };
                      }) ?? []
                    }
                  />
                </FormikProvider>
              )}
            </Box>

            <Button
              sx={{ mt: "8px" }}
              type="submit"
              variant="contained"
              size="small"
              color={isFilterAccepted() ? "inherit" : "primary"}
              disabled={
                Object.values(formik.errors).length || assignments.loading
                  ? true
                  : false
              }
            >
              Применить фильтр
            </Button>
          </Box>
        </form>
      </Box>
    </Box>
  );
};
